import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { tvSeriesData, categories, type TVSeries } from '@/mocks/tvSeries';

const categoryMeta: Record<string, { title: string; description: string }> = {
  all: { title: '全部电视剧', description: '浏览红桃影院收录的全部电视剧资源，国产剧、韩剧、美剧、日剧一应俱全。' },
  chinese: { title: '国产剧全集', description: '最新最全国产电视剧在线观看，古装、现代、悬疑、爱情各类国产剧全集免费追。' },
  korean: { title: '韩剧在线看', description: '热播韩剧全集免费在线观看，爱情、悬疑、复仇、奇幻等各类韩剧同步更新。' },
  american: { title: '美剧精选', description: '高分美剧全集在线观看，科幻、剧情、冒险等类型美剧一网打尽。' },
  japanese: { title: '日剧在线看', description: '精选日剧全集免费在线观看，律政、悬疑、温情等各类型日剧精彩呈现。' },
  hot: { title: '热播推荐', description: '当下最热门的电视剧推荐，大家都在追的热播好剧，不可错过的精彩内容。' },
  new: { title: '最新上线', description: '刚刚上线的全新电视剧，第一时间为你呈现最新最热的影视内容。' },
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'all');
  const [filteredSeries, setFilteredSeries] = useState<TVSeries[]>([]);

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
    }
  }, [slug]);

  useEffect(() => {
    if (activeSlug === 'all') {
      setFilteredSeries(tvSeriesData);
    } else if (activeSlug === 'hot') {
      setFilteredSeries(tvSeriesData.filter(s => s.isHot));
    } else if (activeSlug === 'new') {
      setFilteredSeries(tvSeriesData.filter(s => s.isNew));
    } else {
      setFilteredSeries(tvSeriesData.filter(s => s.categorySlug === activeSlug));
    }
  }, [activeSlug]);

  const meta = categoryMeta[activeSlug] || categoryMeta.all;

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main>
        <div className="pt-20 pb-10 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: meta.title },
              ]}
            />
            <div className="mt-6">
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950">
                {meta.title}
              </h1>
              <p className="text-foreground-500 mt-2 text-sm">{meta.description}</p>
            </div>
          </div>
        </div>

        <div className="py-6 px-4 md:px-6 lg:px-8 bg-background-50 border-b border-background-200 sticky top-16 z-30">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-1.5 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  onClick={() => setActiveSlug(cat.slug)}
                  className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200 border border-background-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </div>

        <section className="py-10 md:py-14 px-4 md:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            {filteredSeries.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-20 h-20 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">暂无该分类的电视剧</p>
              </div>
            ) : (
              <>
                <p className="text-sm text-foreground-500 mb-6">
                  共找到 <strong className="text-foreground-900">{filteredSeries.length}</strong> 部电视剧
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
                  {filteredSeries.map((series) => (
                    <Link
                      key={series.id}
                      to={`/article/${series.id}`}
                      className="group cursor-pointer"
                      data-product-shop
                    >
                      <div className="relative aspect-[2/3] rounded-lg overflow-hidden mb-3">
                        <img
                          src={series.posterImage}
                          alt={`${series.title} - 红桃影院${meta.title}`}
                          title={`${series.title} ${series.category} ${series.year}`}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                        <div className="absolute top-2 left-2">
                          <span className="px-2 py-0.5 bg-primary-500 text-white text-xs font-medium rounded-md">
                            {series.rating}
                          </span>
                        </div>
                        {series.isNew && (
                          <div className="absolute top-2 right-2">
                            <span className="px-2 py-0.5 bg-accent-500 text-white text-xs font-medium rounded-md">NEW</span>
                          </div>
                        )}
                        <div className="absolute bottom-0 left-0 right-0 p-3 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                          <p className="text-white text-xs leading-relaxed line-clamp-2">{series.description}</p>
                        </div>
                      </div>
                      <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors truncate">
                        {series.title}
                      </h3>
                      <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                        <span className="text-xs text-foreground-500">{series.category}</span>
                        <span className="text-xs text-foreground-400">·</span>
                        <span className="text-xs text-foreground-500">{series.episodeCount}集</span>
                        <span className="text-xs text-foreground-400">·</span>
                        <span className="text-xs text-foreground-500">{series.status}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </>
            )}
          </div>
        </section>
      </main>

      <Footer />

      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: `红桃影院 - ${meta.title}`,
          description: meta.description,
          url: `https://hongtao.tv/category/${activeSlug}`,
          isPartOf: { '@type': 'WebSite', name: '红桃影院', url: 'https://hongtao.tv' },
        })}
      </script>
    </>
  );
}